import { api } from "#web/services/api";
import { FieldsFrom } from "#web/types/gadget";
import {
  AvailableShopifyShopSelection,
  JSONValue,
  ShopifyShopRecord,
  UpdateShopifyShopInput,
} from "@gadget-client/keepcart-server";
import { queryOptions } from "@tanstack/react-query";

const selection = {
  alertEmails: true,
  alertType: true,
  betaFeatures: true,
  blockingActive: true,
  blockingReplaceCode: true,
  bulkOperations: {
    edges: {
      node: {
        status: true,
      },
    },
  },
  createdAt: true,
  currency: true,
  customerTagsBlocked: true,
  domain: true,
  duplicateBlockingMode: true,
  exclusionCodes: true,
  extensionBlockingMode: true,
  highRiskCodeCurrencyMinimum: true,
  highRiskCodePercentageMinimum: true,
  id: true,
  initialScrapeStatus: true,
  installedCheckoutExtension: true,
  installedExtensionAnalytics: true,
  myshopifyDomain: true,
  onboardingSurvey: true,
  planName: true,
  subscription: {
    id: true,
    name: true,
    orders: true,
    price: true,
    type: true,
    interval: true,
  },
  version: true,
} satisfies AvailableShopifyShopSelection;

export const ShopQuery = () =>
  queryOptions({
    queryFn: async () =>
      api.shopifyShop.findFirst({
        select: selection,
      }),
    queryKey: ["shop"],
  });

export type Shop = ShopData & {
  alertEmails: JSONValue;
  blockingActive: boolean;
  blockingReplaceCode: string;
  cleanedDomain: null | string;
  shopifyPlus: boolean;
  subscribedTo: (group: string) => boolean;
  update: (input: UpdateShopifyShopInput) => Promise<ShopifyShopRecord>;
};
export type ShopData = FieldsFrom<ShopifyShopRecord<typeof selection>, typeof selection>;

export const makeShop = (data: ShopData): Shop => {
  const SHOPIFY_PLUS_PLAN_KEY = "shopify_plus";
  const shopifyPlus = data.planName === SHOPIFY_PLUS_PLAN_KEY;
  // Remove protocol and www from domain
  const cleanedDomain = data.domain?.replace(/(https?:\/\/)?(www\.)?/, "") ?? null;
  const blockingActive = !!data.blockingActive;
  const blockingReplaceCode = data.blockingReplaceCode ?? "";
  const alertEmails = data.alertEmails ?? [];

  const subscribedTo = (group: string) => data.subscription?.type === group;
  const update = async (input: UpdateShopifyShopInput) =>
    await api.shopifyShop.update(data.id, {
      ...input,
    });

  return {
    ...data,
    alertEmails,
    blockingActive,
    blockingReplaceCode,
    cleanedDomain,
    shopifyPlus,
    subscribedTo,
    update,
  };
};
